import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useSelector } from "react-redux";
import {
  isAdminUser,
  splitLeaveRequestNotifyEmails,
  validateLeaveRequestNotifyEmailList,
  leaveRequestNotifyEmailsDiffer,
} from "../../../app/utils/common/Functions";
import { LEAVE_REQUEST_NOTIFY_RECIPIENT_OPTIONS } from "../../../app/utils/constants/dailyHoursNotifyRecipients";
import {
  getLeaveNotificationConfigAPI,
  sendLeaveNotificationTestAPI,
  updateLeaveNotificationConfigAPI,
} from "../../../Redux/Apis/leaveApi";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import Loader from "../../atoms/Loader/Loader";

const LeaveNotificationSettings = ({ open }) => {
  const { user } = useSelector((state) => state.auth.auth);
  const isAdmin = isAdminUser(user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savedEmails, setSavedEmails] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [error, setError] = useState("");

  const selectOptions = useMemo(() => {
    const saved = splitLeaveRequestNotifyEmails(selectedEmails.join(", "));
    const base = LEAVE_REQUEST_NOTIFY_RECIPIENT_OPTIONS;
    const extra = saved
      .filter((e) => !base.some((o) => o.value.toLowerCase() === e.toLowerCase()))
      .map((e) => ({ label: e, value: e }));
    return [...base, ...extra];
  }, [selectedEmails]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getLeaveNotificationConfigAPI();
        if (cancelled) return;
        const emails = res?.data?.notifyEmails || [];
        const configured = res?.data?.configuredNotifyEmails || emails.join(", ");
        setSavedEmails(configured);
        setSelectedEmails(emails);
      } catch (err) {
        if (!cancelled) {
          showErrorToast(err?.message || "Unable to load leave notification settings.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (!isAdmin || !open) return null;

  const isDirty = leaveRequestNotifyEmailsDiffer(
    selectedEmails.join(", "),
    savedEmails
  );

  const handleSave = async () => {
    setError("");
    const raw = selectedEmails.join(", ");
    if (!validateLeaveRequestNotifyEmailList(raw)) {
      setError("Notification selection could not be validated. Clear and try again.");
      return;
    }
    if (!isAdmin) return;

    setSaving(true);
    try {
      const res = await updateLeaveNotificationConfigAPI({
        notifyEmails: selectedEmails,
      });
      const configured =
        res?.data?.configuredNotifyEmails ?? selectedEmails.join(", ");
      setSavedEmails(configured);
      setSelectedEmails(res?.data?.notifyEmails || selectedEmails);
      showSuccessToast("Leave notification recipients saved.");
    } catch (err) {
      showErrorToast(err?.message || "Unable to save leave notification settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!isAdmin) return;
    setTesting(true);
    try {
      await sendLeaveNotificationTestAPI({});
      showSuccessToast("Test email sent to your account.");
    } catch (err) {
      showErrorToast(err?.message || "SMTP test failed. Check server mail settings.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="leave-notification-settings card-border card-body">
      <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#111827]">
            Leave request email notifications
          </h2>
          <p className="text-sm text-[#6b7280] mt-1 max-w-2xl">
            Choose who receives an email when any employee applies for leave or WFH. Applies to all
            employees. Requires Zoho or SMTP configured on the server.
          </p>
        </div>
        <div className="leave-notification-settings__actions flex flex-wrap gap-2 mt-3 md:mt-0">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving || loading}
            className="px-4 py-2 text-sm rounded-lg bg-btn-primary text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save recipients"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center mt-4">
          <Loader className="!h-[10vh]" />
        </div>
      ) : (
        <div className="mt-4 max-w-xl">
          <label className="form-label">Notification recipients</label>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Select one or more people"
            className="w-full h-10 mt-1 employee-role-select"
            popupClassName="employee-role-select-dropdown"
            options={selectOptions}
            value={selectedEmails}
            onChange={(vals) => {
              setError("");
              setSelectedEmails(Array.isArray(vals) ? vals : []);
            }}
            maxTagCount="responsive"
          />
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default LeaveNotificationSettings;
